import type { IGEMTable } from "../gem-table/gem-table";

export interface IFile {
  created_time: string | number;
  id: string;
  mime_type: string;
  // using unknown instead of any to prevent unnecessarily deep generic type check errors
  metadata: Partial<Record<string, unknown>>;
  size: string;
  created_by: string;
  version_number: string;
  version?: string;
  filename: string;
  unversioned_url: string;
  versioned_url: string;
  description?: string;
}

export interface IFileLink {
  type: "file_link";
  url: string;
  filename: string;
}

export interface IFileSource {
  type: "file_source";
  file_name: string;
  path: string;
}

export interface IFileContentLink {
  pre_signed_read_link: string;
  s3_bucket: string;
  s3_key: string;
  s3_version: string;
}

export interface IUploadRequestData {
  file_name: string;
  metadata: Partial<Record<string, any>>;
  mime_type: string;
  size: number;
  description?: string;
}

interface ICitrineUpload {
  upload_id: string;
  file_name: string;
  s3_key: string;
}

export interface ICitrineUploadRequestResponse {
  temporary_credentials: {
    access_key_id: string;
    secret_access_key: string;
    session_token: string;
  };
  s3_region: string;
  s3_bucket: string;
  uploads: ICitrineUpload[];
  upload_type: string;
}

export type FileLinkIDs = {
  datasetId: string;
  fileId: string;
  fileVersion: string;
} & ({ projectId: string } | { teamId: string });

// http://datasvc.development.citrine-platform.net:8002/api/v1/files/ac1af921-2a96-470f-8423-10e35eacef9d/versions/992390cb-4357-4dfe-b259-27d5abaf394e
const versionedFileUrlRegex = /\/files\/([-\w]+)\/versions\/([-\w]+)/;
// projects/6d028836-7117-491e-a9b5-b43c60fa7989/datasets/18d01f2a-89bd-4fac-b23f-6b2d9c434188/files/c6e4d4bf-41fd-401b-96c5-f4b55614ecf4/versions/2f68bb82-058b-4ccf-bd26-e3b35cfe755d
const fileUrlRegexes = [
  /(?:^|\/)projects\/(?<projectId>[-\w]+)\/datasets\/(?<datasetId>[-\w]+)\/files\/(?<fileId>[-\w]+)\/versions\/(?<fileVersion>[-\w]+)/,
  /(?:^|\/)teams\/(?<teamId>[-\w]+)\/datasets\/(?<datasetId>[-\w]+)\/files\/(?<fileId>[-\w]+)\/versions\/(?<fileVersion>[-\w]+)/,
];

/**
 * extracts fileId and fileVersion from a url
 * returns null for an invalid url
 * for easier destructuring do something like "const { fileId } = extractMetaFromVersionFileUrl(url) || {}"
 * @param {IFile} file
 */
export const extractMetaFromVersionedUrl = (versionedUrl: string) => {
  const match = versionedFileUrlRegex.exec(versionedUrl);
  return match
    ? {
        fileId: match[1],
        fileVersion: match[2],
      }
    : undefined;
};
export const extractMetaFromFile = (file: IFile) =>
  extractMetaFromVersionedUrl(file.versioned_url);

/**
 * Unfortunately necessary to get the real file ID because the given "id" field is the latest version of the file
 * which doesn't work for the delete call for example;
 */
export const getVersionedId = (file: IFile) => {
  const match = extractMetaFromFile(file);

  if (match) {
    return `${match.fileId}/versions/${match.fileVersion}`;
  }
};

export const getFileId = (file: IFile): string | undefined => {
  const match = file.versioned_url && extractMetaFromFile(file);
  if (match) {
    return match.fileId;
  }
  return undefined;
};

/**
 * extracts projectId, datasetId, fileId, and fileVersion from a url.
 * returns null for an invalid url.
 *
 * Useful for extracting the parameters needed to fetch an IFile object starting
 * from an IFileLink object.
 *
 * @param {string} url
 */
export const extractIdsFromFileLinkUrl = (url: string) => {
  for (const regex of fileUrlRegexes) {
    const match = regex.exec(url);
    if (match) {
      return match.groups as FileLinkIDs;
    }
  }
};

export const isFile = (arg?: IFile | IGEMTable): arg is IFile =>
  !!arg && "versionedUrl" in arg;
